/* /admin/email — the email service: provider & sender, deliverability (DNS), message log, template gallery and suppressions. */
import "../account/account.css";
import { useState } from "react";
import { useAdminEmail } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { Alert, Card, Chip, Kv, Loaded, PageHeader, Tabs } from "../../ui";
import { providerById } from "./email/catalog";
import DnsTab from "./email/DnsTab";
import LogTab from "./email/LogTab";
import ProviderTab from "./email/ProviderTab";
import SuppressionsTab from "./email/SuppressionsTab";
import TemplatesTab from "./email/TemplatesTab";

type Tab = "provider" | "dns" | "log" | "templates" | "suppressions";

export default function AdminEmail() {
  const { L } = useI18n();
  const q = useAdminEmail();
  const [tab, setTab] = useState<Tab>("provider");
  return (
    <div data-testid="admin-email">
      <PageHeader title={L("Email", "البريد الإلكتروني")} sub={L("Verification, password reset, billing and HR notifications", "التوثيق واستعادة كلمة المرور وإشعارات الفوترة والموارد البشرية")} />
      <Loaded q={q}>{(c) => {
        const info = providerById(c.provider.provider);
        const log = c.provider.provider === "log";
        return (
          <div className="stack">
            <Card testId="email-status">
              <div className="status-card">
                <span className={"sdot " + (log ? "bad" : "ok")} aria-hidden="true" />
                <div className="grow">
                  <b data-testid="email-status-title">{log ? L("Emails are not being delivered", "لا يتم تسليم الرسائل") : L("Sending with " + (info?.name ?? c.provider.provider), "الإرسال عبر " + (info?.nameAr ?? c.provider.provider))}</b>
                  <div className="sm muted">{log ? L("Log only: messages are recorded in the log below but never leave the server.", "وضع السجل فقط: تُسجل الرسائل أدناه ولا تغادر الخادم.") : L("From ", "من ") + c.fromName + " <" + c.fromAddress + ">"}</div>
                </div>
                <Chip tone={c.source === "database" ? "pri" : undefined} testId="email-source">{c.source === "database" ? L("Set in dashboard", "مضبوط من اللوحة") : L("Server defaults", "إعدادات الخادم")}</Chip>
              </div>
              <div className="mt">
                <Kv items={[
                  [L("Provider", "المزوّد"), <span key="p" data-testid="email-provider">{info ? L(info.name, info.nameAr) : c.provider.provider}</span>],
                  ...(c.fallback ? [[L("Fallback", "الاحتياطي"), providerById(c.fallback.provider)?.name ?? c.fallback.provider]] as [string, string][] : []),
                  [L("Sent today", "أُرسل اليوم"), <span key="s" className="num" data-testid="email-sent-today">{c.sentToday}{c.dailyLimit ? " / " + c.dailyLimit : ""}</span>],
                  [L("Waiting in queue", "في قائمة الانتظار"), <span key="q" className="num" data-testid="email-queued">{c.queued}</span>],
                  ...(c.updatedBy ? [[L("Last changed", "آخر تعديل"), (c.updatedAt ?? "").slice(0, 16).replace("T", " ") + " · " + c.updatedBy]] as [string, string][] : []),
                ]} />
              </div>
              {c.sandboxTo && <div className="mt"><Alert tone="warn" title={L("Sandbox: every email is redirected to " + c.sandboxTo, "وضع التجربة: كل الرسائل تُحوّل إلى " + c.sandboxTo)} testId="email-sandbox-banner" /></div>}
            </Card>
            <Tabs label={L("Email settings", "إعدادات البريد")} value={tab} onChange={setTab} items={[
              ["provider", L("Provider", "المزوّد")], ["dns", L("Deliverability", "التسليم")], ["log", L("Log", "السجل"), c.queued || null],
              ["templates", L("Templates", "القوالب")], ["suppressions", L("Suppressions", "المحظورون")],
            ]} />
            {tab === "provider" && <ProviderTab key={JSON.stringify([c.source, c.updatedAt])} cur={c} onDns={() => setTab("dns")} />}
            {tab === "dns" && <DnsTab cur={c} />}
            {tab === "log" && <LogTab />}
            {tab === "templates" && <TemplatesTab />}
            {tab === "suppressions" && <SuppressionsTab />}
          </div>
        );
      }}</Loaded>
    </div>
  );
}
